/**
 * Telink 键盘 HID 描述符中的报告定义。
 *
 * WebHID 必须以描述符声明的完整长度发送 Output Report。
 */
export const TELINK_KEYBOARD_HID = {
  controlOutputReportId: 0xba,
  controlResponseFeatureReportId: 0x08,
  controlPayloadByteLength: 31,
} as const

/** Telink 键盘接收器控制命令。命令语义来自芯片协议，而非具体产品型号。 */
export const TELINK_KEYBOARD_COMMAND = {
  enterReceiverPairingMode: 0xa0,
  queryReceiverPairingState: 0xa1,
  resetReceiver: 0xac,
  readReceiverFirmwareVersion: 0xa9,
  readKeyboardFirmwareVersion: 0xa3,
  enterKeyboardFirmwareUpdate: 0xa8,
  enterReceiverFirmwareUpdate: 0xb1,
  read_eeprom: [0xa4],
  write_eeprom: [0xa5],
  get_config: [0xd3],
  set_config: [0xd2],
  add_config: [0xa6],
  delete_config: [0xae],
  delete_Lighting: [0xaf],
  init_default_flash: [0xad],
  enter_admin_mode: [0xb8, 0xde, 0xc2, 0xb1, 0xa0],
  exit_admin_mode: [0xb9],
} as const

/** 控制接口的确认状态。收到 pending 时继续等待同一次命令的最终结果。 */
export const TELINK_KEYBOARD_CONTROL_STATUS = {
  acknowledged: 0x01,
  pending: 0x02,
} as const

/** Telink 接收器的键盘设备类型值。 */
export const TELINK_RECEIVER_KEYBOARD_DEVICE_TYPE = 0x02

/**
 * Telink 键盘协议通用配置。
 * ReportId=0xba（controlOutputReportId），响应在 Feature Report Id=0x08。
 * A4 指令只支持 8/16/24 等 8 的倍数的读取长度，使用时注意对齐。
 */
export const PROTOCOL_CONFIG = {
  report_id: TELINK_KEYBOARD_HID.controlOutputReportId,
  feature_report_id: TELINK_KEYBOARD_HID.controlResponseFeatureReportId,
  busy_ack_value: 0x02,
  read_ack_value: 0x01,
} as const

/** 恢复出厂设置可选指令 */
export const INIT_DEFAULT_FLASH = {
  factory: 0x3d,
  admin: 0x4d,
} as const

/**
 * 将短控制命令补齐为 HID 描述符要求的完整数据区。
 *
 * 原生 HID 允许短写，但浏览器会校验 Report 长度；补零是 WebHID 与原生实现保持一致的必要转换。
 */
export function createTelinkKeyboardControlPayload(command: Uint8Array): Uint8Array {
  if (!(command instanceof Uint8Array)) {
    throw new TypeError('Telink 键盘控制命令必须是 Uint8Array')
  }
  if (
    command.byteLength === 0 ||
    command.byteLength > TELINK_KEYBOARD_HID.controlPayloadByteLength
  ) {
    throw new RangeError(
      `Telink 键盘控制命令长度必须在 1-${TELINK_KEYBOARD_HID.controlPayloadByteLength} 字节之间`,
    )
  }

  const payload = new Uint8Array(TELINK_KEYBOARD_HID.controlPayloadByteLength)
  payload.set(command)
  return payload
}

/**
 * 构造 Telink 键盘接收器进入对码模式的命令。
 *
 * V700 键盘使用接收器无线 PID 识别目标型号，不能套用鼠标协议的随机 RF 地址写入流程。
 * 帧格式来自 RapooSystem 的 `BaseProtocol::setTelinkDgPaiMode`：
 * `A0 81 02 FF FF <PID-L> <PID-H>`。
 */
export function createTelinkKeyboardPairingModeCommand(wirelessProductId: number): Uint8Array {
  if (!Number.isInteger(wirelessProductId) || wirelessProductId < 0 || wirelessProductId > 0xffff) {
    throw new RangeError('Telink 键盘无线 PID 必须是 0x0000-0xffff 的整数')
  }

  return new Uint8Array([
    TELINK_KEYBOARD_COMMAND.enterReceiverPairingMode,
    0x81,
    TELINK_RECEIVER_KEYBOARD_DEVICE_TYPE,
    0xff,
    0xff,
    wirelessProductId & 0xff,
    (wirelessProductId >> 8) & 0xff,
  ])
}

/** 构造读取接收器对码状态的命令。 */
export function createTelinkKeyboardPairingStatusCommand(): Uint8Array {
  return new Uint8Array([TELINK_KEYBOARD_COMMAND.queryReceiverPairingState, 0x01])
}

/** 构造退出接收器对码等待状态的命令，避免键盘继续处于无效的等待窗口。 */
export function createTelinkKeyboardPairingExitCommand(): Uint8Array {
  return new Uint8Array([TELINK_KEYBOARD_COMMAND.queryReceiverPairingState, 0x84])
}

/**
 * 构造键盘版本读取命令。
 *
 * V700 有线和无线透传共用固件命令字；无线链路必须将命令加 0x20，避免接收器把它当作本机命令。
 */
export function createTelinkKeyboardFirmwareVersionCommand(isWireless: boolean): Uint8Array {
  return new Uint8Array([
    isWireless ? 0xa5 : 0xff,
    TELINK_KEYBOARD_COMMAND.readKeyboardFirmwareVersion + (isWireless ? 0x20 : 0),
  ])
}

/**
 * 构造进入固件升级程序的命令。
 *
 * 键盘仅允许有线进入升级程序；接收器则使用自身的 B1 命令，二者进入后都会重启为升级接口。
 */
export function createTelinkFirmwareUpdateEntryCommand(isReceiver: boolean): Uint8Array {
  if (isReceiver) {
    return new Uint8Array([TELINK_KEYBOARD_COMMAND.enterReceiverFirmwareUpdate, 0x00])
  }

  return new Uint8Array([0xff, TELINK_KEYBOARD_COMMAND.enterKeyboardFirmwareUpdate])
}
